import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles, Award, Palette, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { convertGoogleDriveLink } from '../utils/imageUtils';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categories, setCategories] = useState([]);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    fetchFeatured();
    fetchSettings();
    fetchCategories();
    
    // Auto-slide hero images
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const fetchFeatured = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      setFeaturedProducts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch featured products', error);
    }
  };
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      // Normalize each hero slide to {desktop, mobile} shape.
      // Legacy string entries are treated as {desktop: str, mobile: ''}.
      const normalize = (item) => {
        if (typeof item === 'string') return { desktop: item, mobile: '' };
        return { desktop: item?.desktop || '', mobile: item?.mobile || '' };
      };
      if (response.data.hero_images && response.data.hero_images.length > 0) {
        setHeroImages(response.data.hero_images.map(normalize).filter(s => s.desktop));
      } else if (response.data.hero_image) {
        setHeroImages([{ desktop: response.data.hero_image, mobile: '' }]);
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
    } finally {
      setHeroLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const [catResponse, prodResponse] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`)
      ]);
      
      const categoryData = catResponse.data.map(cat => {
        const categoryProducts = prodResponse.data.filter(p => p.category === cat.name);
        const productCount = categoryProducts.length;
        return {
          name: cat.name,
          image: cat.image || categoryProducts[0]?.main_image || '',
          // Manual display_count (set by admin) overrides the auto-computed product count.
          count: (cat.display_count !== null && cat.display_count !== undefined) ? cat.display_count : productCount,
        };
      });
      
      setCategories(categoryData);
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  return (
    <div>
      {/* Hero Carousel Section */}
      <section className="relative h-[700px] md:h-[800px] overflow-hidden" data-testid="hero-section">
        {heroLoading || heroImages.length === 0 ? (
          <div className="absolute inset-0 bg-gradient-to-br from-luxury-maroon to-luxury-dark flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-5xl md:text-7xl font-heading font-bold mb-4">Ambika Fashion</h1>
              <p className="text-xl text-white/80">Premium B2B Wholesale</p>
            </div>
          </div>
        ) : (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.7 }}
                className="absolute inset-0"
              >
                <picture className="absolute inset-0 block w-full h-full">
                  {heroImages[currentSlide]?.mobile ? (
                    <source
                      media="(max-width: 767px)"
                      srcSet={convertGoogleDriveLink(heroImages[currentSlide].mobile)}
                    />
                  ) : null}
                  <img
                    src={convertGoogleDriveLink(heroImages[currentSlide]?.desktop)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid={`hero-slide-image-${currentSlide}`}
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            {heroImages.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white p-3 md:p-4 transition-all duration-300 border border-white/20"
                  data-testid="prev-slide"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white p-3 md:p-4 transition-all duration-300 border border-white/20"
                  data-testid="next-slide"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {/* Slide Indicators */}
            {heroImages.length > 1 && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-1 transition-all duration-300 ${
                      index === currentSlide ? 'w-12 bg-luxury-gold' : 'w-6 bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        )}
        
        <div className="relative z-10 h-full flex items-center justify-center text-center text-white px-6">
          <motion.div
            key={`content-${currentSlide}`}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="inline-block mb-4"
            >
              <div className="bg-luxury-gold/20 backdrop-blur-sm border border-luxury-gold/50 px-6 py-2">
                <p className="text-luxury-gold font-medium tracking-widest uppercase text-sm">Exquisite Collection</p>
              </div>
            </motion.div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold mb-6 leading-tight">
              Ambika Fashion
              <span className="block text-luxury-gold mt-2">Luxury Redefined</span>
            </h1>
            <p className="text-xl md:text-2xl mb-10 leading-relaxed max-w-3xl mx-auto font-light">
              Premium B2B wholesale collection of traditional Indian ethnic wear for discerning retailers worldwide
            </p>
            <Link to="/products" data-testid="explore-collections-button">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-luxury-gold hover:bg-luxury-darkGold text-white px-10 py-5 font-medium text-lg transition-all duration-300 shadow-2xl inline-flex items-center space-x-3 group"
              >
                <span>Explore Collections</span>
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Appointment Banner */}
      <section
        className="py-8 md:py-10"
        style={{ backgroundColor: '#F7F3EA', borderTop: '2px solid #C13B2A' }}
        data-testid="appointment-banner"
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-5 md:gap-8 text-center md:text-left">
            <p
              className="font-heading tracking-[0.03em] leading-tight"
              style={{ color: '#3B2F25', fontSize: 'clamp(1.25rem, 1.9vw, 1.625rem)' }}
            >
              For Full Preview&nbsp;—&nbsp;Book An Appointment
            </p>
            <Link
              to="/book"
              className="inline-flex items-center gap-2.5 font-heading transition-all duration-300 group whitespace-nowrap hover:bg-[#C13B2A] hover:text-[#F7F3EA]"
              style={{
                color: '#C13B2A',
                border: '1px solid #C13B2A',
                padding: '10px 20px',
                borderRadius: '3px',
                fontSize: '15px',
                letterSpacing: '0.02em',
                backgroundColor: 'transparent',
              }}
              data-testid="book-appointment-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>Book Now</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Collections Showcase */}
      <section className="py-24 md:py-32 bg-accent" data-testid="collections-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block mb-4"
            >
              <div className="bg-luxury-gold/10 border-l-4 border-luxury-gold px-6 py-2">
                <p className="text-luxury-gold font-semibold tracking-widest uppercase text-sm">Our Collections</p>
              </div>
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-heading font-bold mb-6 text-foreground">
              Browse by Collection
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Discover our curated collections of traditional lehengas, each crafted with meticulous attention to detail
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`category-${category.name.toLowerCase()}`}
                className="w-full"
                style={{
                  flexBasis: 'calc(33.333% - 1.334rem)',
                  minWidth: '280px',
                  maxWidth: '420px',
                }}
              >
                <Link to={`/products?category=${encodeURIComponent(category.name)}`}>
                  <div className="group relative overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500">
                    <div className="aspect-[3/4] overflow-hidden relative">
                      <img
                        src={convertGoogleDriveLink(category.image)}
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      
                      {/* Luxury overlay */}
                      <div className="absolute inset-0 bg-luxury-gold/0 group-hover:bg-luxury-gold/10 transition-all duration-500"></div>
                    </div>
                    
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <h3 className="text-2xl font-heading font-bold text-white mb-2 drop-shadow-lg">
                        {category.name}
                      </h3>
                      <p className="text-luxury-gold font-medium drop-shadow-lg">{category.count} Designs</p>
                      <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <span className="inline-flex items-center text-white text-sm">
                          View Collection <ArrowRight size={16} className="ml-2" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/products" data-testid="view-all-collections-button">
              <button className="border-2 border-luxury-gold text-luxury-gold hover:bg-luxury-gold hover:text-white px-10 py-4 font-semibold text-lg transition-all duration-300">
                View All Collections
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="features-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-heading font-bold mb-6">The Ambika Fashion Promise</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Excellence in every thread, luxury in every detail
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="feature-card-quality"
            >
              <div className="w-20 h-20 bg-luxury-gold/10 flex items-center justify-center mx-auto mb-6 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gold-shimmer animate-shimmer"></div>
                <Sparkles size={40} className="text-luxury-gold relative z-10" />
              </div>
              <h3 className="text-2xl font-heading font-bold mb-4">Premium Craftsmanship</h3>
              <p className="text-muted-foreground leading-relaxed text-lg">
                Each piece is a masterpiece, handcrafted by skilled artisans with decades of expertise
              </p>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              data-testid="feature-card-wholesale"
            >
              <div className="w-20 h-20 bg-luxury-bronze/10 flex items-center justify-center mx-auto mb-6 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gold-shimmer animate-shimmer"></div>
                <Award size={40} className="text-luxury-bronze relative z-10" />
              </div>
              <h3 className="text-2xl font-heading font-bold mb-4">Wholesale Excellence</h3>
              <p className="text-muted-foreground leading-relaxed text-lg">
                Competitive B2B pricing with flexible credit terms designed for your business growth
              </p>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              data-testid="feature-card-custom"
            >
              <div className="w-20 h-20 bg-luxury-gold/10 flex items-center justify-center mx-auto mb-6 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gold-shimmer animate-shimmer"></div>
                <Palette size={40} className="text-luxury-gold relative z-10" />
              </div>
              <h3 className="text-2xl font-heading font-bold mb-4">Bespoke Creations</h3>
              <p className="text-muted-foreground leading-relaxed text-lg">
                Custom design services to bring your unique vision to life with our expert team
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-24 md:py-32 bg-luxury-cream" data-testid="featured-products-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-block mb-4"
              >
                <div className="bg-luxury-gold/10 border-l-4 border-luxury-gold px-6 py-2">
                  <p className="text-luxury-gold font-semibold tracking-widest uppercase text-sm">Bestsellers</p>
                </div>
              </motion.div>
              <h2 className="text-5xl md:text-6xl font-heading font-bold mb-6">Featured Collection</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Handpicked designs that define contemporary luxury
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`featured-product-${index}`}
                >
                  <Link to={`/products/${product.id}`}>
                    <div className="group relative transition-all duration-500 hover:-translate-y-2">
                      <div className="aspect-[3/4] overflow-hidden bg-white shadow-lg">
                        <img
                          src={convertGoogleDriveLink(product.main_image)}
                          alt={product.name}
                          className="w-full h-full transition-transform duration-700 group-hover:scale-110"
                          style={{
                            objectFit: product.image_fit || 'cover',
                            objectPosition: `center ${product.image_position || 'center'}`,
                            transform: `scale(${Math.min(2, Math.max(0.5, product.image_zoom || 1))})`,
                          }}
                        />
                        <div className="absolute inset-0 bg-luxury-gold/0 group-hover:bg-luxury-gold/10 transition-all duration-500"></div>
                      </div>
                      <div className="mt-6 text-center">
                        <h3 className="text-xl font-heading font-bold mb-2 group-hover:text-luxury-gold transition-colors">{product.name}</h3>
                        <p className="text-muted-foreground mb-3 text-sm uppercase tracking-wider">{product.category}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12 p-8 bg-white border-2 border-luxury-gold/30">
              <p className="text-lg text-muted-foreground mb-4">
                <span className="font-semibold text-luxury-gold">Exclusive B2B Pricing</span> - Login to view wholesale prices
              </p>
              <Link to="/auth">
                <button className="bg-luxury-gold hover:bg-luxury-darkGold text-white px-8 py-3 font-semibold transition-all duration-300">
                  Login to View Prices
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-luxury-gradient text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gold-shimmer animate-shimmer"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-heading font-bold mb-6">
            Elevate Your Inventory
          </h2>
          <p className="text-xl mb-10 opacity-90 leading-relaxed">
            Join the elite network of retailers who choose Ambika Fashion for unmatched quality and service
          </p>
          <Link to="/auth" data-testid="get-started-button">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-luxury-gold hover:bg-luxury-cream px-10 py-5 font-bold text-lg transition-all duration-300 shadow-2xl"
            >
              Become a Partner
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
